import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import FilePreview, { formatReaderBytes } from "../../components/FilePreview";
import { LANGUAGE_LABEL, detectLanguage } from "../../components/fileLanguage";
import { useGitLabFileMetadata } from "../../../../hooks/useGitLabFileMetadata";
import { useGitLabFileContent } from "../../../../hooks/useGitLabFileContent";
import { gitlabApiPrefix } from "../../../../components/SupabaseClient";
import { resolveRelativeAssetPath } from "../../../../utils/markdownRenderer";

const GITLAB_REF = "HEAD";

export default function GitLabFilePreview({ projectId, filePath, webUrl }) {
  const [forceOpen, setForceOpen] = useState(false);
  const {
    metadata,
    loading: metaLoading,
    error: metaError,
  } = useGitLabFileMetadata(projectId, filePath);
  const {
    content,
    error: contentError,
    status,
    warning,
    canForceOpen,
  } = useGitLabFileContent(projectId, filePath, metadata, "HEAD", {
    forceOpen,
  });

  useEffect(() => {
    setForceOpen(false);
  }, [filePath]);

  const fileName =
    metadata?.fileName || (filePath ? filePath.split("/").pop() : "");
  const language = useMemo(
    () => detectLanguage(fileName || filePath || ""),
    [fileName, filePath],
  );
  const lineCount = useMemo(
    () => (content ? content.replace(/\r/g, "").split("\n").length : 0),
    [content],
  );

  const baseDir =
    filePath && filePath.includes("/")
      ? filePath.slice(0, filePath.lastIndexOf("/"))
      : "";

  const resolveAssetUrl = useCallback(
    async (relativeSrc) => {
      const resolvedPath = resolveRelativeAssetPath(baseDir, relativeSrc);
      return `${gitlabApiPrefix}/api/v4/projects/${projectId}/repository/files/${encodeURIComponent(resolvedPath)}/raw?ref=${encodeURIComponent(GITLAB_REF)}`;
    },
    [projectId, baseDir],
  );

  if (!filePath) return null;

  return (
    <FilePreview
      fileName={fileName}
      filePath={filePath}
      language={language}
      languageLabel={LANGUAGE_LABEL[language] || "Text"}
      content={content}
      sizeText={metadata ? formatReaderBytes(metadata.size) : ""}
      lineCount={lineCount}
      lastCommit={metadata?.lastCommitMessage || ""}
      loading={metaLoading || status === "loading"}
      error={metaError || contentError}
      previewBlocked={status === "blocked"}
      warning={warning}
      canForceOpen={canForceOpen}
      onForceOpen={() => setForceOpen(true)}
      webUrl={webUrl}
      resolveAssetUrl={resolveAssetUrl}
    />
  );
}

GitLabFilePreview.propTypes = {
  projectId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  filePath: PropTypes.string,
  webUrl: PropTypes.string,
};
